/**
 * @fileoverview Mock response envelope builders.
 * @description Wraps mock success/error bodies in the identity backend's common envelope —
 * `{ httpStatus, message, traceId, data }` on success, `{ httpStatus, httpError, appCode, message,
 * traceId, data: [] }` on failure — shared by every mocked identity endpoint migrated to this
 * contract, so each mock module builds the same shape instead of duplicating it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { ApiEnvelope } from '@/shared/types/http.types'

const HTTP_STATUS_TEXT: Record<number, string> = {
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  409: 'Conflict',
  410: 'Gone',
  429: 'Too Many Requests',
  500: 'Internal Server Error',
  503: 'Service Unavailable',
}

export interface MockErrorEnvelope {
  httpStatus: number
  httpError: string
  appCode: string
  message: string
  traceId: string
  data: []
}

/**
 * @description Builds a mock success envelope around a response payload.
 * @param {T} data - The endpoint's response payload.
 * @param {string} message - A human-readable summary of the result.
 * @returns {ApiEnvelope<T>} The enveloped response body.
 */
export function buildMockSuccessEnvelope<T>(data: T, message: string): ApiEnvelope<T> {
  return { httpStatus: 200, message, traceId: crypto.randomUUID(), data }
}

/**
 * @description Builds a mock error envelope for a failed request.
 * @param {number} httpStatus - The HTTP status code to reply with.
 * @param {string} appCode - The backend's public application error code.
 * @param {string} message - The public message for this appCode.
 * @returns {MockErrorEnvelope} The enveloped error body.
 */
export function buildMockErrorEnvelope(
  httpStatus: number,
  appCode: string,
  message: string,
): MockErrorEnvelope {
  return {
    httpStatus,
    httpError: HTTP_STATUS_TEXT[httpStatus] ?? 'Error',
    appCode,
    message,
    traceId: crypto.randomUUID(),
    data: [],
  }
}
